'use client';

import { useState, useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';
import { getSliderAriaLabelArray } from '@/utils/get-slider-aria-label-array';

type RangeSliderProps = {
  name: 'budget' | 'year';
  min: number;
  max: number;
  value: [number, number];
  onChangeCommitted: (value: [number, number]) => void;
  step?: number;
  className?: string;
};

export const RangeSlider = ({ name, min, max, value, onChangeCommitted, step = 1, className }: RangeSliderProps) => {
  const [displayedValue, setDisplayedValue] = useState(value);

  useEffect(() => {
    setDisplayedValue(value);
  }, [value]);

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleValueChange = (newValue: number[]) => {
    if (Array.isArray(newValue) && newValue.length === 2) {
      setDisplayedValue([newValue[0], newValue[1]]);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const rangeValue: [number, number] = [newValue[0], newValue[1]];
        onChangeCommitted(rangeValue);
      }, 150);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const ariaLabels = getSliderAriaLabelArray(name);

  return (
    <Slider
      value={displayedValue}
      min={min}
      max={max}
      step={step}
      onValueChange={handleValueChange}
      aria-label={`${ariaLabels[0]}, ${ariaLabels[1]}`}
      className={cn('w-full', className)}
    />
  );
};
